/**
 * ToxIntel Copilot™ — Bloomberg-Terminal-style workspace.
 *
 * Layout: dense, dark, three-column.
 *   • Left rail   — session history (terminal-style list)
 *   • Center      — message stream + composer (text/image/PDF inputs)
 *   • Right rail  — current session metadata + scientific protocol notes
 *
 * Aesthetic: minimal masculine luxury enterprise. Solid black canvas, navy
 * accents, a single coral hairline brand accent, monospace metadata,
 * serif headlines, dense info ratio. Avoids beauty-app / wellness / Yuka
 * styling at every level.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Terminal, PaperPlaneRight, Image as ImageIcon, FilePdf,
  Plus, ArrowsClockwise, Lock, Pencil, Trash, ShieldCheck,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_PROMPTS = [
  'Screen 0.5% hydroquinone for Prop 65 and Sephora Clean concerns.',
  'Does PFAS-coated paperboard packaging pass Credo packaging standards?',
  'Review a preservative system of phenoxyethanol + ethylhexylglycerin for retailer compliance.',
  'What MoCRA registration obligations apply to a 200-SKU indie skincare portfolio?',
  'Identify EU Annex II/III concerns in a fragrance blend with linalool, limonene, citral.',
];

export default function CopilotPage() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);     // { id, title, messages: [] }
  const [composing, setComposing] = useState(false);
  const [text, setText] = useState('');
  const [health, setHealth] = useState(null);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const streamRef = useRef(null);

  // ---- data loaders ---------------------------------------------------
  const loadSessions = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/copilot/sessions`, { withCredentials: true });
      setSessions(r.data?.sessions || []);
    } catch (e) { /* swallow — page still usable */ }
  }, []);

  const loadSession = useCallback(async (sid) => {
    if (!sid) { setActive(null); return; }
    try {
      const r = await axios.get(`${API_URL}/api/copilot/sessions/${sid}`, { withCredentials: true });
      setActive(r.data);
      setActiveId(sid);
    } catch (e) {
      toast.error('Could not load session');
    }
  }, []);

  useEffect(() => {
    loadSessions();
    axios.get(`${API_URL}/api/copilot/health`).then(r => setHealth(r.data)).catch(() => { });
  }, [loadSessions]);

  // Auto-scroll to bottom of message stream on new message.
  useEffect(() => {
    if (streamRef.current) {
      streamRef.current.scrollTop = streamRef.current.scrollHeight;
    }
  }, [active?.messages?.length, composing]);

  // ---- actions --------------------------------------------------------
  const startNew = () => { setActive(null); setActiveId(null); setText(''); };

  const sendText = async (overrideText) => {
    const body = (overrideText ?? text).trim();
    if (!body || composing) return;
    setComposing(true);
    setText('');
    // Optimistic local append so the user sees their message immediately.
    const optimistic = {
      id: 'local-' + Date.now(),
      role: 'user',
      content: body,
      attachments: [],
      created_at: new Date().toISOString(),
    };
    setActive((cur) => cur
      ? { ...cur, messages: [...(cur.messages || []), optimistic] }
      : { id: null, title: body.slice(0, 80), input_mode: 'text', messages: [optimistic] });

    try {
      const r = await axios.post(`${API_URL}/api/copilot/query`,
        { text: body, session_id: activeId || undefined },
        { withCredentials: true });
      const sid = r.data.session_id;
      // Replace the optimistic stub by reloading the full session — keeps id mapping clean.
      await loadSession(sid);
      await loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Copilot request failed');
      // Roll back optimistic message.
      setActive((cur) => cur
        ? { ...cur, messages: (cur.messages || []).filter(m => m.id !== optimistic.id) }
        : null);
    } finally {
      setComposing(false);
    }
  };

  const uploadFile = async (file, kind) => {
    if (!file) return;
    setComposing(true);
    const fd = new FormData();
    fd.append('file', file);
    if (activeId) fd.append('session_id', activeId);
    const endpoint = kind === 'image' ? 'upload-image' : 'upload-pdf';
    try {
      const r = await axios.post(`${API_URL}/api/copilot/${endpoint}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadSession(r.data.session_id);
      await loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind} upload failed`);
    } finally {
      setComposing(false);
    }
  };

  const renameSession = async (sid) => {
    const next = window.prompt('Rename session');
    if (!next) return;
    try {
      await axios.post(`${API_URL}/api/copilot/sessions/${sid}/title`,
        { title: next }, { withCredentials: true });
      await loadSessions();
      if (sid === activeId) await loadSession(sid);
    } catch (e) { toast.error('Rename failed'); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm('Delete this session permanently?')) return;
    try {
      await axios.delete(`${API_URL}/api/copilot/sessions/${sid}`, { withCredentials: true });
      toast.success('Session deleted');
      if (sid === activeId) startNew();
      await loadSessions();
    } catch (e) { toast.error('Delete failed'); }
  };

  // ---- render ---------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#050505] text-[#E5E7EB] font-sans" data-testid="copilot-page">
      {/* Terminal-style top strip */}
      <header className="border-b border-white/10 bg-[#0B0B0D] px-6 py-3 flex items-center justify-between sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white">
            ← Portal
          </Link>
          <div className="h-4 w-px bg-white/15" />
          <Terminal size={16} className="text-[#E63946]" weight="duotone" />
          <h1 className="text-sm font-bold tracking-tight">
            ToxIntel <span className="italic text-[#E63946]">Copilot</span>
            <sup className="text-[9px] text-white/40 ml-0.5">™</sup>
          </h1>
          <span className="text-[9px] uppercase tracking-[0.3em] text-white/30 ml-2">
            Scientific Intelligence Console
          </span>
        </div>
        <div className="hidden md:flex items-center gap-3 text-[10px] font-mono text-white/40">
          <Lock size={11} weight="bold" className="text-[#E63946]" />
          <span>{user?.email}</span>
          <span className="text-white/20">·</span>
          <span>{health?.model || 'model: …'}</span>
          {health?.llm_available
            ? <span className="px-1.5 py-0.5 border border-[#10B981]/40 text-[#10B981] uppercase tracking-wider">live</span>
            : <span className="px-1.5 py-0.5 border border-[#E63946]/40 text-[#E63946] uppercase tracking-wider">offline</span>}
        </div>
      </header>

      {/* Three-column workspace */}
      <div className="grid grid-cols-[260px_1fr_280px] gap-0 min-h-[calc(100vh-49px)]">
        {/* LEFT — Sessions rail */}
        <aside className="border-r border-white/10 bg-[#0A0A0D] flex flex-col" data-testid="copilot-sessions">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">Sessions</p>
            <div className="flex gap-1">
              <button onClick={loadSessions}
                className="p-1 text-white/40 hover:text-white"
                title="Refresh" data-testid="copilot-refresh-sessions">
                <ArrowsClockwise size={12} weight="bold" />
              </button>
              <button onClick={startNew}
                className="p-1 text-[#E63946] hover:text-white"
                title="New session" data-testid="copilot-new-session">
                <Plus size={14} weight="bold" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {sessions.length === 0 ? (
              <p className="p-4 text-[11px] text-white/30 leading-relaxed">
                No sessions yet. Start a query to open the first console session.
              </p>
            ) : sessions.map(s => (
              <div key={s.id} className="border-b border-white/5">
                <button onClick={() => loadSession(s.id)}
                  className={`w-full text-left px-4 py-2.5 hover:bg-white/5 ${s.id === activeId ? 'bg-white/[0.06]' : ''}`}
                  data-testid={`copilot-session-${s.id}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="text-[12px] font-semibold truncate">{s.title}</p>
                    <span className="text-[9px] font-mono text-white/30 uppercase">{s.input_mode}</span>
                  </div>
                  <p className="text-[10px] font-mono text-white/30 mt-0.5">
                    {s.message_count} msgs · {new Date(s.updated_at).toLocaleString()}
                  </p>
                </button>
                {s.id === activeId && (
                  <div className="px-4 pb-2 flex gap-3 text-[10px]">
                    <button onClick={() => renameSession(s.id)} className="text-white/40 hover:text-white flex items-center gap-1">
                      <Pencil size={10} /> rename
                    </button>
                    <button onClick={() => deleteSession(s.id)} className="text-[#E63946]/70 hover:text-[#E63946] flex items-center gap-1">
                      <Trash size={10} /> delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* CENTER — Message stream + composer */}
        <main className="flex flex-col h-[calc(100vh-49px)]">
          <div ref={streamRef}
            className="flex-1 overflow-y-auto px-8 py-6 space-y-6"
            data-testid="copilot-stream">
            {!active || (active.messages?.length ?? 0) === 0 ? (
              <EmptyStateHero onPickPrompt={(p) => sendText(p)} />
            ) : (
              active.messages.map(m => (
                <MessageBubble key={m.id} m={m}
                  onPin={() => toast.success('Note pinned to formulation')} />
              ))
            )}
            {composing && (
              <div className="flex items-center gap-3 text-[11px] font-mono text-white/40 animate-pulse" data-testid="copilot-thinking">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#E63946]" />
                analysing · routing through protected intelligence layer…
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-white/10 bg-[#0A0A0D] px-6 py-4">
            <div className="flex items-end gap-2">
              <button onClick={() => fileInputRef.current?.click()}
                disabled={composing}
                className="px-3 py-2 border border-white/15 hover:border-[#E63946] text-white/60 hover:text-white text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-30"
                data-testid="copilot-attach-image">
                <ImageIcon size={13} weight="bold" /> Image
              </button>
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => { uploadFile(e.target.files?.[0], 'image'); e.target.value = ''; }} />
              <button onClick={() => pdfInputRef.current?.click()}
                disabled={composing}
                className="px-3 py-2 border border-white/15 hover:border-[#E63946] text-white/60 hover:text-white text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-30"
                data-testid="copilot-attach-pdf">
                <FilePdf size={13} weight="bold" /> PDF
              </button>
              <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => { uploadFile(e.target.files?.[0], 'pdf'); e.target.value = ''; }} />
              <div className="flex-1 relative">
                <textarea value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendText(); }
                  }}
                  placeholder="Ask the Copilot — formulas, packaging, SDS questions, retailer readiness, regulatory implications…"
                  rows={2}
                  className="w-full bg-[#050505] border border-white/15 text-white text-[13px] px-3 py-2 resize-none focus:outline-none focus:border-[#E63946] font-mono placeholder:text-white/25"
                  disabled={composing}
                  data-testid="copilot-input" />
              </div>
              <button onClick={() => sendText()}
                disabled={composing || !text.trim()}
                className="px-4 py-2.5 bg-[#E63946] hover:bg-[#C9192C] text-black text-[11px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="copilot-send">
                <PaperPlaneRight size={13} weight="bold" /> Send
              </button>
            </div>
            <p className="text-[9px] font-mono text-white/30 mt-2 uppercase tracking-wider">
              Enter to send · Shift+Enter for newline · Intelligence is proprietary and audit-logged
            </p>
          </div>
        </main>

        {/* RIGHT — Protocol panel */}
        <aside className="border-l border-white/10 bg-[#0A0A0D] p-5 hidden lg:block overflow-y-auto" data-testid="copilot-protocol">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={14} className="text-[#E63946]" weight="duotone" />
            <p className="text-[10px] font-bold uppercase tracking-[0.25em]">Operating Protocol</p>
          </div>
          <ProtocolBlock title="Scope" body="Enterprise scientific infrastructure. Retailer readiness, toxicological governance, packaging intelligence, regulatory decision support." />
          <ProtocolBlock title="Output Vocabulary" body={
            <ul className="space-y-0.5 text-[10px] font-mono text-white/60">
              <li>· No significant concern identified</li>
              <li>· Further review recommended</li>
              <li>· Monitor</li>
              <li>· Potential retailer concern</li>
              <li>· Escalation recommended</li>
            </ul>
          } />
          <ProtocolBlock title="Protected" body="Methodologies, scoring math, exposure formulae, read-across logic, database schemas, retailer overlay logic." />
          <ProtocolBlock title="Inputs Supported" body={
            <ul className="space-y-0.5 text-[10px] font-mono text-white/60">
              <li>✓ Text query</li>
              <li>✓ Image (JPEG/PNG/WEBP · ≤ 8 MB)</li>
              <li>✓ PDF (SDS · supplier docs · ≤ 25 MB)</li>
              <li>· Voice — coming next phase</li>
              <li>· Barcode — coming next phase</li>
            </ul>
          } />
        </aside>
      </div>
    </div>
  );
}

// =============================================================
//  Sub-components
// =============================================================
function EmptyStateHero({ onPickPrompt }) {
  return (
    <div className="max-w-2xl">
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#E63946] mb-3">
        SESSION 0 · NEW QUERY
      </p>
      <h2 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Georgia, serif' }}>
        Speak directly to the <span className="italic text-[#E63946]">intelligence layer</span>.
      </h2>
      <p className="text-[13px] text-white/55 leading-relaxed mb-8 max-w-xl">
        ToxIntel Copilot™ orchestrates toxicology, packaging RSL screens, Prop 65 watch-lists,
        retailer compliance overlays, and regulatory intelligence behind a controlled
        conversational surface. Methodology stays proprietary; conclusions stay yours.
      </p>
      <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 mb-3">
        Try an example query
      </p>
      <div className="space-y-1.5">
        {SUGGESTED_PROMPTS.map((p, i) => (
          <button key={i} onClick={() => onPickPrompt(p)}
            className="block w-full text-left px-4 py-3 border border-white/10 hover:border-[#E63946] hover:bg-white/[0.03] text-[12px] text-white/70 hover:text-white transition-colors"
            data-testid={`copilot-suggested-${i}`}>
            <span className="text-[#E63946] font-mono mr-2">»</span>{p}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ m, onPin }) {
  const isUser = m.role === 'user';
  const [pickerOpen, setPickerOpen] = React.useState(false);
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`} data-testid={`copilot-msg-${m.id}`}>
      <div className={`max-w-[80%] ${isUser ? 'order-2' : ''}`}>
        <div className="flex items-baseline gap-2 mb-1">
          <span className={`text-[9px] font-mono uppercase tracking-[0.25em] ${isUser ? 'text-white/40' : 'text-[#E63946]'}`}>
            {isUser ? '› You' : '⌬ ToxIntel'}
          </span>
          <span className="text-[9px] font-mono text-white/25">
            {new Date(m.created_at).toLocaleTimeString()}
          </span>
          {!isUser && !String(m.id || '').startsWith('local-') && (
            <button onClick={() => setPickerOpen(v => !v)}
              className="ml-auto text-[9px] font-mono uppercase tracking-[0.25em] text-white/30 hover:text-[#E63946] transition-colors"
              data-testid={`copilot-pin-${m.id}`}>
              ◆ Pin to formulation
            </button>
          )}
        </div>
        <div className={`px-4 py-3 border ${isUser
          ? 'bg-[#0A0A0D] border-white/10 text-white/90'
          : 'bg-[#0C0F18] border-[#E63946]/25 text-[#E5E7EB]'} text-[13px] leading-relaxed whitespace-pre-wrap`}>
          {m.content}
        </div>
        {(m.attachments || []).map((a, i) => (
          <div key={i} className="mt-1 text-[10px] font-mono text-white/40 uppercase tracking-wider">
            {a.kind === 'image' ? '📷' : '📄'} {a.filename} · {a.size ? `${Math.round(a.size / 1024)} KB` : ''}
          </div>
        ))}
        {pickerOpen && (
          <PinPicker messageId={m.id} onClose={() => setPickerOpen(false)} onPinned={onPin} />
        )}
      </div>
    </div>
  );
}

/* Inline formulation picker — fetches the user's formulations and pins the
 * selected assistant message as a durable Copilot note. Kept terse to match
 * the terminal aesthetic. */
function PinPicker({ messageId, onClose, onPinned }) {
  const [formulations, setFormulations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState('');
  const [note, setNote] = React.useState('');
  const [pinning, setPinning] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/formulations`, { withCredentials: true });
        const list = Array.isArray(r.data) ? r.data : (r.data?.formulations || []);
        setFormulations(list);
        if (list.length > 0) setSelected(list[0].id);
      } catch (_) { /* user may have no formulations yet */ }
      finally { setLoading(false); }
    })();
  }, []);

  const pin = async () => {
    if (!selected) { toast.error('Pick a formulation first'); return; }
    setPinning(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/copilot/messages/${messageId}/pin`,
        { formulation_id: selected, note: note.trim() || null },
        { withCredentials: true },
      );
      toast.success('Pinned to formulation');
      onClose();
      if (typeof onPinned === 'function') onPinned(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pin failed');
    } finally { setPinning(false); }
  };

  return (
    <div className="mt-2 border border-[#E63946]/35 bg-[#0A0A0D] p-3 space-y-2" data-testid="copilot-pin-picker">
      <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-[#E63946]">
        Pin this finding to a formulation
      </p>
      {loading ? (
        <p className="text-[11px] text-white/40 font-mono">Loading formulations…</p>
      ) : formulations.length === 0 ? (
        <p className="text-[11px] text-white/40 font-mono">
          You have no formulations yet — create one from <span className="text-[#E63946]">New Assessment</span> first.
        </p>
      ) : (
        <>
          <select value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="w-full bg-[#050505] border border-white/15 text-white text-[12px] px-2 py-1.5 font-mono focus:outline-none focus:border-[#E63946]"
            data-testid="copilot-pin-formulation-select">
            {formulations.map(f => (
              <option key={f.id} value={f.id}>
                {f.metadata?.product_name || f.product_name || `Formulation ${String(f.id).slice(-6)}`}
                {f.version ? ` · v${f.version}` : ''}
              </option>
            ))}
          </select>
          <input value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note (e.g. 'Q3 reformulation review')"
            maxLength={500}
            className="w-full bg-[#050505] border border-white/15 text-white text-[11px] px-2 py-1.5 placeholder:text-white/25 font-mono focus:outline-none focus:border-[#E63946]"
            data-testid="copilot-pin-note-input" />
          <div className="flex gap-2 justify-end">
            <button onClick={onClose}
              className="px-3 py-1 text-[10px] uppercase tracking-wider text-white/50 hover:text-white">
              Cancel
            </button>
            <button onClick={pin} disabled={pinning || !selected}
              className="px-3 py-1 bg-[#E63946] hover:bg-[#C9192C] text-black text-[10px] uppercase tracking-wider font-bold disabled:opacity-30"
              data-testid="copilot-pin-confirm-btn">
              {pinning ? 'Pinning…' : 'Pin'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function ProtocolBlock({ title, body }) {
  return (
    <div className="mb-5">
      <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-white/35 mb-1.5">{title}</p>
      {typeof body === 'string'
        ? <p className="text-[11px] text-white/60 leading-relaxed">{body}</p>
        : body}
    </div>
  );
}
